import React, { useMemo, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useContracts, useClients } from '../../contexts/DataContext';
import { useUser } from '../../components/UserContext';
import { Heading } from '../../components/heading';
import { Text } from '../../components/text';
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '../../components/table';
import TablePager from '../../components/TablePager';
import { saldosDeCliente } from '../../utils/saldosCliente';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

const fmtMXN = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n || 0);

export function SaldosCreditosClientes() {
  const { contractsList } = useContracts();
  const { clientsById, clientsList } = useClients();
  const { userData } = useUser();
  const userOrg = userData?.org;

  const [expandedIds, setExpandedIds] = useState(() => new Set());
  // clienteId -> { loading, creditos } — fetched lazily on first expand so the
  // report doesn't fan out one Firestore read per cliente upfront.
  const [creditosById, setCreditosById] = useState({});
  const [page, setPage] = useState(1);
  const perPage = 10;

  const rows = useMemo(() => {
    const contractsByClient = new Map();
    contractsList.forEach((c) => {
      if (!c.clientId) return; // convenio contracts have no cliente to owe/hold saldos
      const list = contractsByClient.get(c.clientId) || [];
      list.push(c);
      contractsByClient.set(c.clientId, list);
    });

    const out = [];
    contractsByClient.forEach((contracts, clientId) => {
      const saldos = saldosDeCliente(contracts);
      if (!saldos.saldoEnContra && !saldos.saldoAFavor) return;
      out.push({
        clientId,
        nombre: clientsById[clientId]?.nombreEmpresa || 'Sin nombre',
        ...saldos,
      });
    });
    return out.sort((a, b) => b.saldoEnContra - a.saldoEnContra);
  }, [contractsList, clientsById]);

  const totals = useMemo(() => {
    const t = { enContra: 0, aFavor: 0, conDeuda: 0 };
    rows.forEach((r) => {
      t.enContra += r.saldoEnContra;
      t.aFavor += r.saldoAFavor;
      if (r.saldoEnContra > 0) t.conDeuda += 1;
    });
    return t;
  }, [rows]);

  const pagedRows = rows.slice((page - 1) * perPage, page * perPage);

  const toggleExpanded = (clientId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) {
        next.delete(clientId);
      } else {
        next.add(clientId);
        if (!creditosById[clientId]) {
          setCreditosById((m) => ({ ...m, [clientId]: { loading: true, creditos: [] } }));
          // Org filter required for the rules to prove the list — see the
          // creditos block in firestore.rules.
          getDocs(query(collection(db, 'clientes', clientId, 'creditos'), where('org', '==', userOrg)))
            .then((snap) => {
              const creditos = snap.docs
                .map((d) => ({ id: d.id, ...d.data() }))
                .filter((c) => (Number(c.disponible) || 0) > 0);
              setCreditosById((m) => ({ ...m, [clientId]: { loading: false, creditos } }));
            })
            .catch(() => {
              setCreditosById((m) => ({ ...m, [clientId]: { loading: false, creditos: [] } }));
            });
        }
      }
      return next;
    });
  };

  const StatTile = ({ label, value, sub, tone }) => (
    <div className={`rounded-lg border p-4 ${tone || 'bg-white border-azul-surelia/20'}`}>
      <Text className="text-xs text-azul-surelia/70 mb-1">{label}</Text>
      <Text className="text-xl font-bold text-azul-surelia tabular-nums">{value}</Text>
      {sub && <Text className="text-xs text-zinc-500 mt-0.5">{sub}</Text>}
    </div>
  );

  return (
    <div>
      <div className="mb-4">
        <Heading level={2} className="text-xl text-azul-surelia">Saldos de clientes</Heading>
        <Text className="text-sm text-azul-surelia/70">Por pagar y a favor con cada afianzadora, al día de hoy</Text>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <StatTile
          label="Por pagar a afianzadoras"
          value={fmtMXN(totals.enContra)}
          sub={`${totals.conDeuda} ${totals.conDeuda === 1 ? 'cliente' : 'clientes'}`}
          tone={totals.enContra > 0 ? 'bg-orange-50 border-orange-200' : null}
        />
        <StatTile
          label="Saldo a favor (facturas)"
          value={fmtMXN(totals.aFavor)}
          tone={totals.aFavor > 0 ? 'bg-verde-lima/10 border-verde-lima/30' : null}
        />
        <StatTile label="Clientes con saldos" value={rows.length} sub={`de ${clientsList.length}`} />
      </div>

      {rows.length === 0 ? (
        <Text className="text-sm text-zinc-500 py-8 text-center">Sin saldos pendientes</Text>
      ) : (
        <>
          <div className="overflow-x-auto border border-azul-surelia/15 rounded-lg">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader className="w-8"></TableHeader>
                  <TableHeader>Cliente</TableHeader>
                  <TableHeader className="text-right">Por pagar</TableHeader>
                  <TableHeader className="text-right">A favor</TableHeader>
                  <TableHeader className="text-right">Neto</TableHeader>
                  <TableHeader className="text-right">Facturas pendientes</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRows.map((row) => {
                  const expanded = expandedIds.has(row.clientId);
                  const creditosState = creditosById[row.clientId];
                  return (
                    <React.Fragment key={row.clientId}>
                      <TableRow
                        className="cursor-pointer hover:bg-azul-surelia/5"
                        onClick={() => toggleExpanded(row.clientId)}
                      >
                        <TableCell className="py-3 pr-0">
                          {expanded ? (
                            <ChevronDownIcon className="w-4 h-4 text-zinc-400" />
                          ) : (
                            <ChevronRightIcon className="w-4 h-4 text-zinc-400" />
                          )}
                        </TableCell>
                        <TableCell className="py-3">
                          <Text className="text-sm font-medium text-azul-surelia">{row.nombre}</Text>
                        </TableCell>
                        <TableCell className="py-3 text-right">
                          <Text className={`text-sm tabular-nums ${row.saldoEnContra > 0 ? 'text-orange-600 font-semibold' : 'text-zinc-300'}`}>
                            {row.saldoEnContra > 0 ? fmtMXN(row.saldoEnContra) : '—'}
                          </Text>
                        </TableCell>
                        <TableCell className="py-3 text-right">
                          <Text className={`text-sm tabular-nums ${row.saldoAFavor > 0 ? 'text-green-700 font-semibold' : 'text-zinc-300'}`}>
                            {row.saldoAFavor > 0 ? fmtMXN(row.saldoAFavor) : '—'}
                          </Text>
                        </TableCell>
                        <TableCell className="py-3 text-right">
                          <Text className={`text-sm font-semibold tabular-nums ${row.neto < 0 ? 'text-orange-600' : 'text-azul-surelia'}`}>
                            {fmtMXN(row.neto)}
                          </Text>
                        </TableCell>
                        <TableCell className="py-3 text-right">
                          <Text className="text-sm text-zinc-600 tabular-nums">{row.facturasPendientes}</Text>
                        </TableCell>
                      </TableRow>
                      {expanded && (
                        <TableRow className="bg-azul-surelia/5">
                          <TableCell colSpan={6} className="py-3 px-6">
                            <div className="space-y-1 mb-2">
                              {row.porAfianzadora.map((a) => (
                                <div
                                  key={a.afianzadora}
                                  className="flex flex-wrap items-center gap-x-4 gap-y-1 bg-white rounded-lg border border-zinc-200 px-3 py-1.5"
                                >
                                  <span className="text-sm text-zinc-700 min-w-[160px] truncate">{a.afianzadora}</span>
                                  <span className="text-xs text-zinc-500">
                                    Por pagar: <span className={`font-semibold tabular-nums ${a.saldoEnContra > 0 ? 'text-orange-600' : 'text-zinc-400'}`}>{fmtMXN(a.saldoEnContra)}</span>
                                  </span>
                                  <span className="text-xs text-zinc-500">
                                    A favor: <span className={`font-semibold tabular-nums ${a.saldoAFavor > 0 ? 'text-green-700' : 'text-zinc-400'}`}>{fmtMXN(a.saldoAFavor)}</span>
                                  </span>
                                </div>
                              ))}
                            </div>
                            {creditosState?.loading ? (
                              <Text className="text-xs text-zinc-500">Cargando créditos…</Text>
                            ) : creditosState?.creditos?.length > 0 ? (
                              <div>
                                <Text className="text-xs font-medium text-azul-surelia mb-1">Créditos vivos</Text>
                                <div className="space-y-1">
                                  {creditosState.creditos.map((c) => (
                                    <div
                                      key={c.id}
                                      className="flex flex-wrap items-center gap-x-4 gap-y-1 bg-verde-lima/10 rounded-lg border border-verde-lima/30 px-3 py-1.5"
                                    >
                                      <span className="text-sm text-zinc-700 min-w-[160px] truncate">{c.afianzadora || 'Sin afianzadora'}</span>
                                      <span className="text-xs text-zinc-600 tabular-nums">
                                        {fmtMXN(c.disponible)} disponible de {fmtMXN(c.monto)}
                                      </span>
                                      {c.origen?.numeroFianza && (
                                        <span className="text-xs font-mono text-zinc-500">{c.origen.numeroFianza}</span>
                                      )}
                                      {c.fecha && (
                                        <span className="text-xs text-zinc-500 ml-auto">
                                          {new Date(c.fecha).toLocaleDateString('es-MX')}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <TablePager total={rows.length} page={page} onPage={setPage} perPage={perPage} itemLabel="clientes" />
        </>
      )}
    </div>
  );
}
